import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { GripHorizontal, Pin, X } from "lucide-react";
import {
    PAGE_TABS_EVENT,
    getHeaderTabs,
    removeHeaderTab,
    removeChildTab,
    reorderHeaderTabs,
    reorderChildTabs,
    getTabHref,
    toggleChildTabPin,
} from "./pageTabsStorage";
import { getTopMenuIcon } from "../../layouts/topMenuIcons";

function isSamePath(currentPath, tab) {
    const href = getTabHref(tab);
    if (!href) return false;
    return currentPath === href || currentPath.startsWith(`${href}/`);
}

function findActiveHeaderIndex(headerTabs, pathname) {
    if (!headerTabs.length) return -1;
    const exact = headerTabs.findIndex((h) => isSamePath(pathname, h));
    if (exact >= 0) return exact;
    for (let i = 0; i < headerTabs.length; i++) {
        const matched = (headerTabs[i].childTabs ?? []).some((c) => isSamePath(pathname, c));
        if (matched) return i;
    }
    return -1;
}

function PageTabs() {
    const [headerTabs, setHeaderTabs] = useState(() => getHeaderTabs());
    const location = useLocation();
    const navigate = useNavigate();
    const [lastClickedHeaderKey, setLastClickedHeaderKey] = useState(null);

    useEffect(() => {
        const handler = (e) => {
            setHeaderTabs(e.detail?.headerTabs ?? getHeaderTabs());
        };
        window.addEventListener(PAGE_TABS_EVENT, handler);
        const storageHandler = (e) => {
            if (e.key === "pageTabs") setHeaderTabs(getHeaderTabs());
        };
        window.addEventListener("storage", storageHandler);
        return () => {
            window.removeEventListener(PAGE_TABS_EVENT, handler);
            window.removeEventListener("storage", storageHandler);
        };
    }, []);

    const routeHeaderIdx = useMemo(
        () => findActiveHeaderIndex(headerTabs, location.pathname),
        [headerTabs, location.pathname]
    );

    const activeHeaderIdx = useMemo(() => {
        if (routeHeaderIdx >= 0) return routeHeaderIdx;
        if (lastClickedHeaderKey != null) {
            const idx = headerTabs.findIndex(
                (h) => h.url === lastClickedHeaderKey
            );
            if (idx >= 0) return idx;
        }
        return headerTabs.length > 0 ? headerTabs.length - 1 : -1;
    }, [routeHeaderIdx, lastClickedHeaderKey, headerTabs]);

    const activeHeader = activeHeaderIdx >= 0 ? headerTabs[activeHeaderIdx] : null;
    const childTabs = activeHeader?.childTabs ?? [];

    // 헤더 탭 핸들러
    const handleHeaderClick = useCallback((tab) => {
        setLastClickedHeaderKey(tab.url);
        navigate(getTabHref(tab));
    }, [navigate]);

    const handleHeaderClose = useCallback((e, tab) => {
        e.preventDefault();
        e.stopPropagation();
        const wasActive = isSamePath(location.pathname, tab) ||
            (tab.childTabs ?? []).some((c) => isSamePath(location.pathname, c));
        const next = removeHeaderTab({ url: tab.url });
        if (wasActive && next.length > 0) {
            const last = next[next.length - 1];
            navigate(getTabHref(last));
        }
    }, [location.pathname, navigate]);

    const handleHeaderDragEnd = useCallback((result) => {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        reorderHeaderTabs(result.source.index, result.destination.index);
    }, []);

    // 하위 탭 핸들러
    const handleChildClick = useCallback((tab) => {
        navigate(getTabHref(tab));
    }, [navigate]);

    const handleChildClose = useCallback((e, tab) => {
        e.preventDefault();
        e.stopPropagation();
        if (!activeHeader) return;
        const wasActive = isSamePath(location.pathname, tab);
        removeChildTab({
            url: tab.url,
            headerUrl: activeHeader.url,
        });
        if (wasActive) {
            navigate(getTabHref(activeHeader));
        }
    }, [activeHeader, location.pathname, navigate]);

    const handleChildDragEnd = useCallback((result) => {
        if (!result.destination || !activeHeader) return;
        if (result.source.index === result.destination.index) return;
        reorderChildTabs(
            { url: activeHeader.url },
            result.source.index,
            result.destination.index
        );
    }, [activeHeader]);

    const handleChildPinToggle = useCallback((e, tab) => {
        e.preventDefault();
        e.stopPropagation();
        if (!activeHeader) return;
        toggleChildTabPin({
            url: tab.url,
            headerUrl: activeHeader.url,
        });
    }, [activeHeader]);

    return (
        <>
            <DragDropContext onDragEnd={handleHeaderDragEnd}>
                <Droppable droppableId="page-tabs-header" direction="horizontal">
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="page-tabs-row page-tabs-header"
                        >
                            {headerTabs.map((tab, index) => {
                                const active = index === activeHeaderIdx;
                                const draggableId = `h-${tab.url}-${index}`;
                                return (
                                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                                        {(prov, snapshot) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={`page-tab is-header ${active ? "is-active" : ""} ${snapshot.isDragging ? "is-dragging" : ""} ${tab.pinned ? "is-pinned" : ""}`}
                                                onClick={() => handleHeaderClick(tab)}
                                                title={tab.title}
                                            >
                                                <span
                                                    className="page-tab-handle"
                                                    {...prov.dragHandleProps}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="드래그하여 순서 변경"
                                                >
                                                    <GripHorizontal size={12} strokeWidth={2} />
                                                </span>
                                                {tab.icon && (
                                                    <span className="page-tab-icon" aria-hidden="true">
                                                        {getTopMenuIcon(tab.icon)}
                                                    </span>
                                                )}
                                                <span className="page-tab-title">{tab.title}</span>
                                                <button
                                                    type="button"
                                                    className="page-tab-close"
                                                    onClick={(e) => handleHeaderClose(e, tab)}
                                                    aria-label="헤더 탭 닫기"
                                                >
                                                    <X size={12} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <DragDropContext onDragEnd={handleChildDragEnd}>
                <Droppable droppableId="page-tabs-child" direction="horizontal">
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="page-tabs-row page-tabs-child"
                        >
                            {childTabs.map((tab, index) => {
                                const active = isSamePath(location.pathname, tab);
                                const draggableId = `c-${tab.url}-${index}`;
                                return (
                                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                                        {(prov, snapshot) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={`page-tab is-child ${active ? "is-active" : ""} ${snapshot.isDragging ? "is-dragging" : ""} ${tab.pinned ? "is-pinned" : ""}`}
                                                onClick={() => handleChildClick(tab)}
                                                title={tab.title}
                                            >
                                                <span
                                                    className="page-tab-handle"
                                                    {...prov.dragHandleProps}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="드래그하여 순서 변경"
                                                >
                                                    <GripHorizontal size={12} strokeWidth={2} />
                                                </span>
                                                <span className="page-tab-title">{tab.title}</span>
                                                <button
                                                    type="button"
                                                    className={`page-tab-pin ${tab.pinned ? "is-pinned" : ""}`}
                                                    onClick={(e) => handleChildPinToggle(e, tab)}
                                                    aria-label={tab.pinned ? "하위 탭 고정 해제" : "하위 탭 고정"}
                                                    aria-pressed={!!tab.pinned}
                                                >
                                                    <Pin
                                                        size={12}
                                                        strokeWidth={2}
                                                        fill={tab.pinned ? "currentColor" : "none"}
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="page-tab-close"
                                                    onClick={(e) => handleChildClose(e, tab)}
                                                    aria-label="하위 탭 닫기"
                                                >
                                                    <X size={12} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
}

export default PageTabs;
